import { useState, type FormEvent } from 'react';
import { Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog.js';
import { Button } from '@/components/ui/Button.js';
import { Label } from '@/components/ui/Label.js';
import { api, ApiError, type TwoFactorEnrollResponse } from '@/api/client.js';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onActivated: () => void;
}

/**
 * Three-stage TOTP enrolment dialog:
 *
 *   1. "scan"      — POST /enroll, show QR + secret + 6-digit input
 *   2. "codes"     — on /activate success, show the 10 recovery codes ONCE
 *   3. (closes)    — caller refreshes status query
 *
 * The challenge with TOTP enrolment is the user has exactly one chance to
 * scan the QR (or copy the secret) and one chance to capture the recovery
 * codes. We block dialog close on the codes step until they've at least
 * copied them to clipboard, since the codes don't appear in the database
 * in plaintext form ever again.
 */
export function TwoFactorEnrollDialog({ open, onOpenChange, onActivated }: Props): JSX.Element {
  const [stage, setStage] = useState<'scan' | 'codes'>('scan');
  const [enrollData, setEnrollData] = useState<TwoFactorEnrollResponse | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  function reset() {
    setStage('scan');
    setEnrollData(null);
    setCode('');
    setRecoveryCodes([]);
    setSubmitting(false);
    setLoading(false);
    setError(null);
    setCopied(false);
  }

  // Fetch the enrol payload (QR + secret) on first open of the dialog. We
  // hold off until the user opens the dialog rather than fetching ambiently,
  // because every call mints a brand-new secret server-side — we don't want
  // to burn one when the user only hovered the button.
  async function startEnrol() {
    setLoading(true);
    setError(null);
    try {
      const data = await api.auth.twoFactor.enroll();
      setEnrollData(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start enrolment. Try again.');
    } finally {
      setLoading(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    if (!enrollData) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.auth.twoFactor.activate({ secret: enrollData.secret, code });
      setRecoveryCodes(res.recoveryCodes);
      setStage('codes');
      onActivated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not activate. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function copyCodes() {
    await navigator.clipboard.writeText(recoveryCodes.join('\n'));
    setCopied(true);
    toast.success('Recovery codes copied');
    setTimeout(() => setCopied(false), 2_000);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onOpenChange(false);
        } else {
          onOpenChange(o);
          if (!enrollData) void startEnrol();
        }
      }}
    >
      <DialogContent>
        {stage === 'scan' ? (
          <>
            <DialogHeader>
              <DialogTitle>Set up two-factor authentication</DialogTitle>
              <DialogDescription>
                Scan the QR with an authenticator app (1Password, Google Authenticator, Authy, etc.)
                — or paste the secret manually — then enter the 6-digit code your app shows.
              </DialogDescription>
            </DialogHeader>
            {loading || !enrollData ? (
              <div className="flex justify-center py-12 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <form className="space-y-4" onSubmit={submitCode}>
                <div className="flex flex-col items-center gap-3">
                  <div
                    className="rounded-md border border-border bg-white p-3"
                    // Server-rendered SVG; safe — comes from our backend.
                    dangerouslySetInnerHTML={{ __html: enrollData.qrSvg }}
                  />
                  <div className="w-full space-y-1.5">
                    <Label className="text-xs">Manual entry secret</Label>
                    <code className="block break-all rounded-md border border-border bg-muted px-3 py-2 font-mono text-xs">
                      {enrollData.secret}
                    </code>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="enrol-code">6-digit code from your app</Label>
                  <input
                    id="enrol-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-center font-mono text-lg tracking-widest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
                {error && (
                  <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive dark:text-destructive-foreground">
                    {error}
                  </p>
                )}
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting || code.length !== 6}>
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Activate'}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Save your recovery codes</DialogTitle>
              <DialogDescription>
                You won't see these again. Print them, screenshot them, or paste them somewhere safe.
                Each can be used once if you lose access to your authenticator.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 rounded-md border border-border bg-muted/40 p-3 font-mono text-sm">
                {recoveryCodes.map((c) => (
                  <code key={c} className="select-all">
                    {c}
                  </code>
                ))}
              </div>
              <Button onClick={copyCodes} variant="secondary" className="w-full">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied' : 'Copy all'}
              </Button>
              <p className="text-xs text-muted-foreground">
                You can regenerate codes later from this page — doing so invalidates this set.
              </p>
            </div>
            <DialogFooter>
              <Button
                onClick={() => {
                  reset();
                  onOpenChange(false);
                }}
                disabled={!copied}
              >
                {copied ? 'Done' : 'Copy them first'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
